import React, { useState, useEffect, ChangeEvent, useMemo } from 'react';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import { useTheme } from 'styled-components';

type CustomInputMoneyProps = {
  name: string;
  title: string;
  value?: number | string;
  readOnly?: boolean;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;

  textAlign?: 'left' | 'center' | 'right';
  required?: boolean;
  showSymbol?: boolean;
  'data-testid'?: boolean | string;
};

const CustomInputMoney: React.FC<CustomInputMoneyProps> = ({
  name,
  title,
  value = 0,
  readOnly = false,
  onChange,
  textAlign = 'right',
  required = false,
  showSymbol = true,
  'data-testid': dataTestId,
}) => {
  const [displayValue, setDisplayValue] = useState('');
  const theme = useTheme();

  const controlId = name;

  const testId = useMemo(() => {
    if (typeof dataTestId === 'string') return dataTestId;
    return dataTestId ? `input-money-${controlId}` : undefined;
  }, [dataTestId, controlId]);

  useEffect(() => {
    if (value !== undefined && value !== null) {
      const numericValue =
        typeof value === 'string' ? parseFloat(value.replace(',', '.')) : value;
      setDisplayValue(formatCurrency(numericValue));
    }
  }, [value]);

  const formatCurrency = (num: number | string): string => {
    let n = typeof num === 'number' ? num : parseFloat(num);
    if (isNaN(n)) {
      n = 0;
    }

    return (
      (showSymbol ? 'R$ ' : '') +
      n.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (rawValue === '') {
      setDisplayValue(showSymbol ? 'R$ 0,00' : '0,00');
      if (onChange) {
        onChange({ target: { name, value: 0 } } as any);
      }
      return;
    }

    const numericValue = parseInt(rawValue, 10) / 100;
    const formattedValue = numericValue.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

    setDisplayValue(showSymbol ? `R$ ${formattedValue}` : formattedValue);

    if (onChange) {
      onChange({ target: { name, value: numericValue.toFixed(2) } } as any);
    }
  };

  return (
    <FormGroup controlId={name}>
      <FormLabel
        style={{ color: theme.colors.botaoConfirmar }}
        data-testid={testId ? `${testId}-label` : undefined}
      >
        {required ? (
          <>
            <span
              style={{ color: theme.colors.botaoConfirmar, fontWeight: 'bold' }}
            >
              {title}
            </span>
            <span style={{ color: theme.colors.vermelho, fontWeight: 'bold' }}>
              *
            </span>
          </>
        ) : (
          title
        )}
      </FormLabel>
      <FormControl
        type="text"
        name={name}
        inputMode="numeric"
        value={displayValue}
        onChange={handleChange}
        readOnly={readOnly}
        data-testid={testId}
        style={{
          textAlign,
          backgroundColor: readOnly ? '#e9ecef' : theme.colors.white,
        }}
      />
    </FormGroup>
  );
};

export default CustomInputMoney;
